import classNames from "classnames";
import { useFormik } from "formik";
import { useEffect, useRef, useState } from "react";
import CustomSelect from "shared/components/common/customSelect";
import { JobRole, modifiers } from "shared/utils/enum";
import { QuestionnaireStep2VS } from "shared/utils/validation";
import ShowMoreText from "react-show-more-text";
import styles from "./style.module.scss";
import {
  GetAllDropdownsWithOptionsService,
  GetLevelofWorkDropdownsService,
} from "shared/services/questionnaire";
import {
  GetQuestionStatement,
  hasChanges,
  mergeArrays,
} from "shared/utils/helper";
import { useDispatch } from "react-redux";
import { setQuestionnaireReducer } from "shared/redux/reducers/questionnaire";

interface formikValuesInterface {
  jobs: QuestionnaireStep2Type[];
}

interface QuestionnaireStep2Props {
  formikRef: any;
  formData: any;
  setStep: (val: number) => void;
  setLoading: (val: boolean) => void;
  setFormData: (val: any) => void;
  stepRef?: any;
  setAddEvent: (val: boolean) => void;
}

const QuestionnaireStep2 = ({
  formikRef,
  formData,
  setStep,
  setLoading,
  setFormData,
  stepRef,
  setAddEvent,
}: QuestionnaireStep2Props) => {
  const dispatch = useDispatch();
  const initialValues: formikValuesInterface = {
    jobs: [
      {
        jobTitle: "",
        levelWork: null,
        modifier: null,
        definition: "",
        output: "",
        alldropDowns: [],
      },
    ],
  };

  const modifierOptions = [
    {
      title: "-",
      Icon: null,
      action: (index: number) => {
        setFieldValue(`jobs[${index}].modifier`, {
          name: modifiers.minus,
          value: modifiers.minus,
        });
        let currentRole = formData[index]?.roleType?.value;
        let currentDropdownId = formik.values.jobs[index]?.levelWork?.value;
        let score = handleModifier(
          currentRole,
          modifiers.minus,
          currentDropdownId
        );
        setFieldValue(`jobs[${index}].output`, score);
        setFieldTouched(`jobs[${index}].modifier`, false);
      },
    },
    {
      title: "=",
      Icon: null,
      action: (index: number) => {
        setFieldValue(`jobs[${index}].modifier`, {
          name: modifiers.equal,
          value: modifiers.equal,
        });
        let currentRole = formData[index]?.roleType?.value;
        let currentDropdownId = formik.values.jobs[index]?.levelWork?.value;
        let score = handleModifier(
          currentRole,
          modifiers.equal,
          currentDropdownId
        );
        setFieldValue(`jobs[${index}].output`, score);
        setFieldTouched(`jobs[${index}].modifier`, false);
      },
    },
    {
      title: "+",
      Icon: null,
      action: (index: number) => {
        setFieldValue(`jobs[${index}].modifier`, {
          name: modifiers.plus,
          value: modifiers.plus,
        });
        let currentRole = formData[index]?.roleType?.value;
        let currentDropdownId = formik.values.jobs[index]?.levelWork?.value;
        let score = handleModifier(
          currentRole,
          modifiers.plus,
          currentDropdownId
        );
        setFieldValue(`jobs[${index}].output`, score);
        setFieldTouched(`jobs[${index}].modifier`, false);
      },
    },
  ];
  const managerDropdowns = useRef<any[]>([]);
  const individualDropdowns = useRef<any[]>([]);
  const [managerLevelOptions, setManagerLevelOptions] = useState<any[]>([]);
  const [individualLevelOptions, setIndividualLevelOptions] = useState<any[]>(
    []
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: QuestionnaireStep2VS,
    onSubmit: () => {
      handleSubmissions();
    },
  });

  const { values, touched, errors, setFieldValue, setFieldTouched } = formik;

  const handleModifier = (
    role: number,
    modifier: string,
    selectedDropdownId: string
  ) => {
    let dropdowns = [];
    if (Number(role) === Number(JobRole.management)) {
      dropdowns = managerDropdowns.current;
    } else {
      dropdowns = individualDropdowns.current;
    }
    for (let k = 0; k < dropdowns?.length; k++) {
      if (dropdowns[k]?.id === selectedDropdownId) {
        let scoresArr = dropdowns[k]?.scores;
        for (let j = 0; j < scoresArr?.length; j++) {
          if (scoresArr[j]?.modifier === modifier) {
            return scoresArr[j]?.score;
          }
        }
      }
    }
  };

  const handleSubmissions = async () => {
    setLoading(true);
    let currentJobs = formik.values.jobs;
    for (let i = 0; i < formik.values.jobs.length; i++) {
      let currentOptionsId = values.jobs[i]?.levelWork?.value;
      let currentModifier = values.jobs[i]?.modifier?.name;
      try {
        let response = await GetAllDropdownsWithOptionsService({
          level_id: currentOptionsId,
          modifier: currentModifier,
        });
        currentJobs[i].alldropDowns = response?.data?.score_options;
        formik.setFieldValue(
          `jobs[${i}].alldropDowns`,
          response?.data?.score_options
        );
        if (i === formik.values.jobs.length - 1) {
          let finalArr = mergeArrays(2, formData, currentJobs);
          setFormData(finalArr);
          setStep(3);
          if (stepRef) {
            dispatch(
              setQuestionnaireReducer({ formData: finalArr, lastStep: 3 })
            );
            stepRef.current = 3;
          }
          setLoading(false);
        }
      } catch (err) {
        setLoading(false);
        break;
      }
    }
  };

  const handleGetLevelOfWorkDropdowns = () => {
    GetLevelofWorkDropdownsService()
      .then(({ data }) => {
        if (data?.data) {
          managerDropdowns.current = data?.data?.options?.second;
          individualDropdowns.current = data?.data?.options?.first;
          let firstOptions = individualDropdowns.current?.map((item: any) => {
            return {
              title: item?.short_des,
              Icon: null,
              action: (index: number) => {
                setFieldValue(`jobs[${index}].definition`, item?.long_des);
                setFieldValue(`jobs[${index}].levelWork`, {
                  name: item?.short_des,
                  value: item?.id,
                });
                setFieldValue(`jobs[${index}].modifier`, null);
                setFieldValue(`jobs[${index}].output`, 0);
                setFieldTouched(`jobs[${index}].levelWork`, false);
              },
            };
          });

          let secondOptions: any[] = [];
          for (let i = 0; i < managerDropdowns.current.length; i++) {
            const item = managerDropdowns.current[i];
            let obj = {
              title: item?.short_des,
              Icon: null,
              action: (index: number) => {
                setFieldValue(`jobs[${index}].definition`, item?.long_des);
                setFieldValue(`jobs[${index}].levelWork`, {
                  name: item?.short_des,
                  value: item?.id,
                });
                setFieldValue(`jobs[${index}].modifier`, null);
                setFieldValue(`jobs[${index}].output`, 0);
                setFieldTouched(`jobs[${index}].levelWork`, false);
              },
            };
            secondOptions.push(obj);
          }

          setIndividualLevelOptions(firstOptions);
          setManagerLevelOptions(secondOptions);
        }
      })
      .catch((err) => {});
  };

  useEffect(() => {
    handleGetLevelOfWorkDropdowns();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    formikRef.current = formik;
    // eslint-disable-next-line
  }, [formik]);

  useEffect(() => {
    if (formData) {
      let jobs = formData;
      let fields: any = [];
      for (let k = 0; k < jobs?.length; k++) {
        let obj = {
          jobTitle: jobs?.[k]?.jobTitle,
          levelWork: jobs?.[k]?.q1?.levelWork ? jobs?.[k]?.q1?.levelWork : null,
          modifier: jobs?.[k]?.q1?.modifier ? jobs?.[k]?.q1?.modifier : null,
          definition: jobs?.[k]?.q1?.definition,
          output: jobs?.[k]?.q1?.output,
          alldropDowns: jobs?.[k]?.q1?.alldropDowns
            ? jobs?.[k]?.q1?.alldropDowns
            : [],
        };
        fields.push(obj);
      }
      formik.setFieldValue("jobs", fields);
    }

    // eslint-disable-next-line
  }, [formData]);

  useEffect(() => {
    if (hasChanges(values.jobs, initialValues.jobs?.[0])) {
      setAddEvent?.(true);
    }
    // eslint-disable-next-line
  }, [values.jobs]);

  return (
    <div className={classNames(styles.stepContainer)}>
      <div className={classNames(styles.head)}>
        <p>Knowledge & Skill (1 of 3) – Level of Work & Work Modifier</p>
        <h1>
          <span>Q1.</span> {GetQuestionStatement(1)}
        </h1>
      </div>
      <form
        className={classNames(styles.tableWrapper)}
        onSubmit={(e) => e.preventDefault()}
      >
        <table className="table" style={{ minWidth: "800px", margin: "0px" }}>
          <thead>
            <tr style={{ height: "44px" }}>
              <th className={classNames(styles.tableHead)}>Job Title</th>
              <th className={classNames(styles.tableHead)}>
                Level of Work <span>*</span>
              </th>
              <th className={classNames(styles.tableHead)}>
                Modifier <span>*</span>
              </th>
              <th className={classNames(styles.tableHead)}>Definition</th>
              <th className={classNames(styles.tableHead)}>RAS Output</th>
            </tr>
          </thead>
          <tbody>
            {values?.jobs.map((job, index) => {
              return (
                <RowItem
                  key={index}
                  index={index}
                  formData={formData}
                  job={job}
                  touched={touched}
                  errors={errors}
                  managerLevelOptions={managerLevelOptions}
                  individualLevelOptions={individualLevelOptions}
                  modifierOptions={modifierOptions}
                />
              );
            })}
          </tbody>
        </table>
      </form>
    </div>
  );
};

const RowItem = ({
  formData,
  index,
  job,
  touched,
  errors,
  managerLevelOptions,
  individualLevelOptions,
  modifierOptions,
}: any) => {
  const currentRole = formData[index]?.roleType?.value;
  return (
    <tr>
      <td
        className={classNames(styles.tableContentLabel)}
        style={{ width: "15%" }}
      >
        <div className={classNames(styles.titleContainer)}>
          <label>{job.jobTitle}</label>
        </div>
      </td>
      <td
        className={classNames(styles.tableContentLabel)}
        style={{ width: "23%" }}
      >
        <CustomSelect
          placeholder="Select level of work"
          options={
            currentRole === JobRole.management
              ? managerLevelOptions
              : individualLevelOptions
          }
          customInputContainer={classNames(styles.selectionContainer)}
          notRequiredMargin
          customOptionsContainer={classNames(styles.optionsContainer)}
          value={job?.levelWork?.name}
          error={
            touched.jobs?.[index]?.levelWork &&
            //@ts-ignore
            errors.jobs?.[index]?.levelWork
          }
          tabIndex={index}
        />
      </td>
      <td
        className={classNames(styles.tableContentLabel)}
        style={{ width: "18%" }}
      >
        <CustomSelect
          placeholder="Select modifier"
          options={modifierOptions}
          customInputContainer={classNames(styles.selectionContainer)}
          notRequiredMargin
          customOptionsContainer={classNames(styles.optionsContainer)}
          value={job?.modifier?.name}
          error={
            touched.jobs?.[index]?.modifier &&
            //@ts-ignore
            errors.jobs?.[index]?.modifier
          }
          tabIndex={index}
        />
      </td>
      <td
        className={classNames(styles.tableContentLabel)}
        style={{ width: "35%" }}
      >
        <div className={classNames(styles.descContainer)}>
          <ShowMoreText
            lines={3}
            more="Show more"
            less="Show less"
            expanded={false}
            anchorClass="show-more-less-clickable"
            truncatedEndingComponent={"... "}
          >
            <label>{job?.definition}</label>
          </ShowMoreText>
        </div>
      </td>
      <td
        className={classNames(styles.tableContentLabel)}
        style={{ width: "10%" }}
      >
        <div className={classNames(styles.scoreContainer)}>
          <label>{job.output ? job.output : 0}</label>
        </div>
      </td>
    </tr>
  );
};

export default QuestionnaireStep2;
